import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpService } from 'src/app/services/http.service';
import { ErrorService } from '../error.service';

/**
 * Dev-only page for verifying the exception-handling tiers by hand. Each button hits a `/dev/errors/*`
 * backend trigger; the global interceptor + {@link ErrorService} then drive the display. This page also
 * renders the `fieldErrors` signal so the validation tier (not yet rendered elsewhere) is observable. Reached at `/dev/errors`, registered only when `!environment.production`.
 */
@Component({
  selector: 'app-error-playground',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="container py-4" style="max-width: 880px;">
      <h2 class="mb-1">Error Playground</h2>
      <p class="text-muted">
        Kun udvikling. Hver knap udløser en serverfejl via <code>/dev/errors/*</code>; interceptoren +
        ErrorService bestemmer visningen.
      </p>

      <div class="d-flex flex-wrap gap-2 my-3">
        <button class="btn btn-outline-primary" (click)="trigger('validation')">400 Validation (direkte)</button>
        <button class="btn btn-outline-primary" (click)="trigger('validation-pipeline')">400 Validation (pipeline)</button>
        <button class="btn btn-outline-secondary" (click)="trigger('notfound')">404 NotFound (modal, advarsel)</button>
        <button class="btn btn-outline-secondary" (click)="trigger('conflict')">409 Conflict (modal, advarsel)</button>
        <button class="btn btn-outline-warning" (click)="trigger('business?status=403')">403 Forbidden (modal)</button>
        <button class="btn btn-outline-warning" (click)="trigger('tenantdb?tenantId=99')">404 Tenant-DB (modal)</button>
        <button class="btn btn-outline-secondary" (click)="trigger('business?status=418')">418 Business (modal, advarsel)</button>
        <button class="btn btn-outline-danger" (click)="trigger('unhandled')">500 Unhandled (/error)</button>
        <button class="btn btn-outline-danger" (click)="trigger('nested')">500 Nested (inner chain)</button>
      </div>

      <div class="d-flex flex-wrap gap-2 mb-4">
        <button class="btn btn-success" (click)="errorService.success('Det lykkedes!', 'Success')">Toast: success</button>
        <button class="btn btn-info" (click)="errorService.info('Til orientering.', 'Info')">Toast: info</button>
        <button class="btn btn-warning" (click)="errorService.warning('Kunne ikke kopiere.', 'Transient')">Toast: warning</button>
        <button class="btn btn-outline-secondary" (click)="errorService.notify('Vælg venligst en skabelon.')">Client-side: modal (advarsel)</button>
        <button class="btn btn-outline-warning" (click)="errorService.notify('Du har ikke adgang til denne funktion.', { tier: 'modal' })">Client-side: modal</button>
        <button class="btn btn-dark" (click)="errorService.goToErrorPage()">Fatal: gå til /error</button>
      </div>

      <p class="text-muted small">
        Recoverable 4xx og blokerende fejl vises i en modal, der skal lukkes. Feltfejl (400) er
        formspecifikke og vises herunder.
      </p>

      <!-- Tier: inline field errors (validation 400) -->
      @if (fieldErrorList().length) {
        <div class="alert alert-danger" role="alert">
          <div class="d-flex justify-content-between">
            <strong>Feltfejl (validation)</strong>
            <button class="btn-close" aria-label="Luk" (click)="errorService.clearFieldErrors()"></button>
          </div>
          <ul class="mb-0">
            @for (entry of fieldErrorList(); track entry.field) {
              <li><code>{{ entry.field }}</code>: {{ entry.messages.join(' ') }}</li>
            }
          </ul>
        </div>
      }

      <!-- Debug: last parsed error -->
      @if (errorService.lastError(); as e) {
        <details class="mt-3">
          <summary>Seneste parsed AppError (severity: {{ e.severity }})</summary>
          <pre class="bg-light border rounded p-2 small">{{ pretty(e) }}</pre>
        </details>
      }
    </div>
  `,
})
export class ErrorPlaygroundComponent {
  readonly errorService = inject(ErrorService);
  private readonly http = inject(HttpService);

  readonly fieldErrorList = computed(() => {
    const fe = this.errorService.fieldErrors();
    return fe ? Object.entries(fe).map(([field, messages]) => ({ field, messages })) : [];
  });

  trigger(path: string): void {
    // The interceptor handles display; swallow the rethrown rejection so it doesn't hit the console handler.
    this.http.get(`${environment.adminConfig.apiUri}/dev/errors/${path}`).catch(() => undefined);
  }

  pretty(value: unknown): string {
    try {
      return JSON.stringify(value, (_k, v) => (v instanceof Error ? `${v.name}: ${v.message}` : v), 2);
    } catch {
      return String(value);
    }
  }
}
